import type { AccessibleOU } from '@/models/FuelUserAccess';

const treeViewUtils = {
  /**
   * Flattens a node to itself and all children, recursively.
   * @param node The node to flatten
   * @param propertyName The name of the property that contains child nodes. Defaults to 'children'.
   * @param list An empty list to populate (used for recursion)
   * @returns A flat list of objects with all children, grandchildren etc in one list.
   */
  flatten(node: object, propertyName = 'children', list: AccessibleOU[] | null = null) {
    if (node == null) {
      return [];
    }
    if (list == null) {
      list = [];
    }
    list.push(node);
    const children = node[propertyName];
    if (children.length > 0) {
      for (let i = 0; i < children.length; i++) {
        const c = children[i];
        treeViewUtils.flatten(c, propertyName, list);
      }
    }
    return list;
  },

  /**
   * Gets a single node from the tree
   * @param ouTree The tree to search.
   * @param ouId The id to find.
   * @param itemKey The name of the property the id is called.
   * @returns A single node that matches the itemKey with the id provided
   */
  getOUTreeNodes(ouTree, ouIds: string[], itemKey = 'ouId') {
    // console.log('getOUTreeNode', { ouTree, ouId });
    if (ouTree == null || ouTree.length == 0) {
      return null;
    }
    const toReturn = [];
    for (let k = 0; k < ouIds.length; k++) {
      for (let i = 0; i < ouTree.length; i++) {
        const found = treeViewUtils.findChildByKey(ouTree[i], ouIds[k], itemKey);
        if (found != null && toReturn.findIndex((x) => x.ouId === found.ouId) === -1) {
          toReturn.push(found);
        }
      }
    }
    return toReturn;
  },

  /**
   * Gets a single node from the tree
   * @param ouTree The tree to search.
   * @param ouId The id to find.
   * @param itemKey The name of the property the id is called.
   * @returns A single node that matches the itemKey with the id provided
   */
  getOUTreeNode(ouTree, ouId, itemKey = 'ouId') {
    // console.log('getOUTreeNode', { ouTree, ouId });
    if (ouTree == null || ouTree.length == 0) {
      return null;
    }
    for (let i = 0; i < ouTree.length; i++) {
      const found = treeViewUtils.findChildByKey(ouTree[i], ouId, itemKey);
      if (found) {
        return found;
      }
    }
    return null;
  },

  findChildByKey(node, value, itemKey = 'ouId') {
    // console.log('findChildByKey', { node, value, itemKey });
    if (node == null) {
      return null;
    }
    if (node[itemKey] === value) {
      return node;
    }
    if (node.children) {
      for (let i = 0; i < node.children.length; i++) {
        const c = node.children[i];
        const found = treeViewUtils.findChildByKey(c, value, itemKey);
        if (found) {
          // console.log('findChildByKey found', { found });
          return found;
        }
      }
    }
    return null;
  },

  findChildByValue(node, value) {
    // console.log('findChildByValue', { node, value });
    if (node == null) {
      return null;
    }
    if (node == value) {
      return node;
    }
    if (node.children) {
      for (let i = 0; i < node.children.length; i++) {
        const c = node.children[i];
        const found = treeViewUtils.findChildByValue(c, value);
        if (found) {
          return found;
        }
      }
    }
    return null;
  },

  createDataTree(dataset) {
    const hashTable = Object.create(null);
    dataset.forEach((aData) => (hashTable[aData.ouId] = { ...aData, children: [] }));
    const dataTree = [];
    dataset.forEach((aData) => {
      if (aData.parentOuId && hashTable[aData.parentOuId]) {
        hashTable[aData.parentOuId].children.push(hashTable[aData.ouId]);
      } else {
        dataTree.push(hashTable[aData.ouId]);
      }
    });
    return dataTree;
  },
  assignCount(arr, index = 0) {
    if (index < arr.length) {
      arr[index].count = treeViewUtils.countChildren(arr[index].children);
      if (arr[index].children.length) {
        return treeViewUtils.assignCount(arr[index].children, 0);
      }
      return treeViewUtils.assignCount(arr, index + 1);
    }
  },
  filterBy(arr, query) {
    return query
      ? arr.reduce((acc, item) => {
        if (item.children?.length) {
          const filtered = treeViewUtils.filterBy(item.children, query);
          if (filtered.length) return [...acc, { ...item, children: filtered }];
        }

        const { children, ...itemWithoutChildren } = item;
        return item.ouName?.toLowerCase().includes(query.toLowerCase()) ? [...acc, itemWithoutChildren] : acc;
      }, [])
      : arr;
  },
  getIDsExpandFilter(node) {
    let store = [];
    const { children } = node;
    if (!children || children.length === 0) {
      return store;
    }
    if (node.parentOuId === null) {
      store = [node.ouId];
    }
    if (children) {
      children.map((child) => store.push(child.ouId));
    }
    node.children.map((child) => treeViewUtils.getIDsExpandFilter(child));
    return store;
  },
  countChildren(children = []) {
    let total = 0;
    for (const child of children) {
      total++;
      const childrenCount = treeViewUtils.countChildren(child.children);
      total += childrenCount;
    }
    return total;
  },
};
export default treeViewUtils;
